import { useCallback, useEffect, useRef, useState } from "react";
import type { ChatMessage, WsClientMessage, WsServerMessage } from "../lib/types";

type ConnState = "connecting" | "open" | "reconnecting" | "auth_required" | "closed";

const BACKOFF_STEPS = [1000, 2000, 5000, 10000, 30000];

// V0 emits `rate_limited_by_v0` (per Reponse-V0 2026-05-12). Init prompt §
// Error handling spec also mentions `throttled`. Both lock input 60s.
const RATE_LIMIT_CODES = new Set(["rate_limited_by_v0", "throttled"]);
const RATE_LIMIT_LOCKOUT_MS = 60_000;

export interface ChatSocketApi {
  state: ConnState;
  sessionId: string | null;
  userId: string | null;
  messages: ChatMessage[];
  assistantPending: boolean;
  inputLockedUntil: number | null;
  send(text: string): void;
}

export function useChatWebSocket(wsUrl: string): ChatSocketApi {
  const [state, setState] = useState<ConnState>("connecting");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [assistantPending, setAssistantPending] = useState(false);
  const [inputLockedUntil, setInputLockedUntil] = useState<number | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const attemptRef = useRef(0);
  const reconnectTimer = useRef<number | null>(null);

  const connect = useCallback(() => {
    setState((s) => (s === "open" ? s : attemptRef.current === 0 ? "connecting" : "reconnecting"));
    let ws: WebSocket;
    try {
      ws = new WebSocket(wsUrl);
    } catch {
      scheduleReconnect();
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      attemptRef.current = 0;
      setState("open");
    };

    ws.onmessage = (ev) => {
      let msg: WsServerMessage;
      try {
        msg = JSON.parse(ev.data);
      } catch {
        return;
      }
      switch (msg.type) {
        case "session":
          setSessionId(msg.session_id);
          setUserId(msg.user_id);
          break;
        case "history":
          setMessages(msg.messages.map((m) => ({ ...m })));
          break;
        case "ack":
          // user message confirmed persisted server-side; assistant pending starts
          setAssistantPending(true);
          break;
        case "assistant_message":
          setAssistantPending(false);
          setMessages((prev) => [
            ...prev,
            {
              message_id: msg.message_id,
              role: "assistant",
              text: msg.text,
              suggestions: msg.suggestions,
            },
          ]);
          break;
        case "system":
          setAssistantPending(false);
          if (RATE_LIMIT_CODES.has(msg.error_code)) {
            setInputLockedUntil(Date.now() + RATE_LIMIT_LOCKOUT_MS);
          }
          setMessages((prev) => [
            ...prev,
            {
              message_id: `sys-${Date.now()}`,
              role: "system",
              text: msg.text,
              error_code: msg.error_code,
            },
          ]);
          break;
        case "pong":
          break;
      }
    };

    ws.onclose = (ev) => {
      wsRef.current = null;
      // 1008 (policy violation) is what we'd use for auth-rejected upstream;
      // but the upgrade handler returns 401 before WS handshake, which means
      // the browser sees onclose without ever opening. We surface auth_required
      // when the very first attempt closes without opening.
      if (ev.code === 1008 || (attemptRef.current === 0 && state === "connecting")) {
        setState("auth_required");
        return;
      }
      scheduleReconnect();
    };

    ws.onerror = () => {
      // onclose follows; reconnect logic handled there.
    };
  }, [wsUrl, state]);

  const scheduleReconnect = useCallback(() => {
    const delay = BACKOFF_STEPS[Math.min(attemptRef.current, BACKOFF_STEPS.length - 1)];
    attemptRef.current += 1;
    setState("reconnecting");
    if (reconnectTimer.current !== null) window.clearTimeout(reconnectTimer.current);
    reconnectTimer.current = window.setTimeout(() => {
      connect();
    }, delay);
  }, [connect]);

  useEffect(() => {
    connect();
    return () => {
      if (reconnectTimer.current !== null) window.clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
    // connect is stable enough; we want one mount-time effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = useCallback((text: string) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const trimmed = text.trim();
    if (!trimmed) return;
    const payload: WsClientMessage = { type: "user_message", text: trimmed };
    ws.send(JSON.stringify(payload));
    setMessages((prev) => [
      ...prev,
      { message_id: `local-${Date.now()}`, role: "user", text: trimmed, pending: true },
    ]);
  }, []);

  return { state, sessionId, userId, messages, assistantPending, inputLockedUntil, send };
}
